import { type AppConfig, GROWTHBOOK_API_HOST } from '@fuze/portal-core';
import { type Attributes, GrowthBook, GrowthBookProvider, useGrowthBook } from '@growthbook/growthbook-react';
import type { ReactNode } from 'react';

import type { AppFeatures } from './app-features';

export type ExperimentationClient = GrowthBook<AppFeatures>;

type ExperimentationProviderProps = { client: ExperimentationClient; children: ReactNode };

type FeatureFlagResult<Key extends keyof AppFeatures> = {
  value: AppFeatures[Key];
  /** True until GrowthBook has answered (or forever when there is no client key); `value` is the default meanwhile. */
  isLoading: boolean;
};

/** One GrowthBook client per app. Without a client key (local dev, tests) nothing is fetched and every flag is its default. */
const create = (config: AppConfig): ExperimentationClient => {
  const attributes: Attributes = { environment: config.environment };
  const clientKey = config.growthbookClientKey;

  if (clientKey === undefined) {
    return new GrowthBook<AppFeatures>({ attributes });
  }

  const client = new GrowthBook<AppFeatures>({
    apiHost: GROWTHBOOK_API_HOST,
    clientKey,
    attributes,
    // Flags and remote config only. Visual, URL-redirect and JS-injection experiments change the DOM from outside
    // the bundle; a REMS portal never runs them (and the CSP would block them anyway).
    disableVisualExperiments: true,
    disableUrlRedirectExperiments: true,
    disableJsInjection: true,
  });

  void client.init({ timeout: 2000, streaming: true }).then((result) => {
    if (!result.success) {
      // Not reported: flags fall back to defaults, which is the designed behaviour for an unreachable GrowthBook.
      console.warn('GrowthBook did not load; using flag defaults', result.error);
    }
  });

  return client;
};

const Provider = ({ client, children }: ExperimentationProviderProps) => (
  <GrowthBookProvider growthbook={client}>{children}</GrowthBookProvider>
);

/**
 * Read one flag. Screens call this and pass the value down; modules never read flags themselves. Until GrowthBook
 * has loaded (or always, without a client key) the value is `defaultValue`, or `false` when none is given, as in
 * clients.
 */
const useFeatureFlag = <Key extends keyof AppFeatures>(
  key: Key,
  defaultValue?: AppFeatures[Key],
): FeatureFlagResult<Key> => {
  const client = useGrowthBook<AppFeatures>();
  // evalFeature returns null when the flag is unknown or not yet loaded; the generated type says what it will be.
  const value = (client.evalFeature(key).value ?? defaultValue ?? false) as AppFeatures[Key];
  return { value, isLoading: !client.ready };
};

/**
 * The app's feature-flag surface, shaped like clients' `Experimentation` namespace:
 *   Experimentation.create(config)     once, in App
 *   <Experimentation.Provider client>  in AppProviders
 *   Experimentation.useFeatureFlag(k)  in screens
 * Attributes (user id, role) are set on the client with `client.setAttributes(...)` by the session provider.
 */
export const Experimentation = { create, Provider, useFeatureFlag };
